import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Disc3, ArrowRight } from 'lucide-react';

const SEO_LINKS = [
  { path: '/free-spinning-record-video-maker', label: 'Free Spinning Record Video Maker' },
  { path: '/how-to-make-a-spinning-record-video', label: 'How to Make a Spinning Record Video' },
  { path: '/instagram-video-dimensions', label: 'Instagram Video Dimensions' },
  { path: '/mp3-to-video', label: 'MP3 to Video' },
];

function setMeta(name, content, attr = 'name') {
  let tag = document.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

// Sets document.title + description/OG/Twitter meta for this route, and
// restores the previous title on unmount so navigating back to "/" (a
// plain CRA index.html, no per-route server rendering) shows the right title.
export function useSeo(title, description) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;
    setMeta('description', description);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    return () => { document.title = prevTitle; };
  }, [title, description]);
}

export default function SeoPageLayout({ children }) {
  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="border-b border-neutral-200 bg-white">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="seo-header-logo-link">
            <div className="w-8 h-8 rounded-full bg-neutral-900 flex items-center justify-center">
              <Disc3 className="w-[18px] h-[18px] text-white" strokeWidth={2} />
            </div>
            <span className="text-lg font-semibold text-neutral-900 tracking-tight">Waxify</span>
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm font-medium bg-neutral-900 hover:bg-neutral-800 text-white px-4 py-2 rounded-full transition-colors"
            data-testid="seo-header-cta-link"
          >
            Make a video <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-5 py-12">
        {children}
      </article>

      <footer className="max-w-3xl mx-auto px-5 pb-12 pt-6 border-t border-neutral-200">
        <p className="text-xs text-neutral-400 mb-3">More from Waxify</p>
        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-neutral-500">
          {SEO_LINKS.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className="hover:text-neutral-800 hover:underline"
              data-testid={`seo-footer-link-${l.path.slice(1)}`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      </footer>
    </div>
  );
}

export const seoTextStyles = {
  h1: 'text-4xl sm:text-5xl font-semibold tracking-tight text-neutral-900',
  h2: 'text-base md:text-lg font-semibold text-neutral-900 mt-8 mb-2',
  p: 'text-base text-neutral-600 leading-relaxed mt-3',
  ul: 'list-disc pl-5 space-y-1.5 text-base text-neutral-600 leading-relaxed mt-3',
  lead: 'text-base text-neutral-500 leading-relaxed mt-4',
};
